import type { ReactElement } from 'react';

import type { MessageReasoning, ToolCall } from '@harness/protocol';

function ToolRow({ call }: { call: ToolCall }): ReactElement {
  const elapsed =
    call.startedAt !== undefined && call.endedAt !== undefined
      ? `${((call.endedAt - call.startedAt) / 1000).toFixed(2)}s`
      : null;

  return (
    <li className="reasoning__tool">
      <div className="reasoning__tool-head">
        <span className="mono">{call.tool}</span>
        <span
          className={`badge ${
            call.status === 'running'
              ? 'badge--signal'
              : call.status === 'error'
                ? 'badge--fault'
                : ''
          }`}
        >
          {call.status}
        </span>
        {elapsed !== null && <span className="mono muted">{elapsed}</span>}
      </div>
      {call.input !== undefined && (
        <div className="reasoning__tool-line muted">
          in: <span className="mono">{call.input}</span>
        </div>
      )}
      {call.steps !== undefined && call.steps.length > 0 && (
        <ul className="reasoning__steps">
          {call.steps.map((step: string, index: number) => (
            <li key={`${String(index)}-${step}`}>{step}</li>
          ))}
        </ul>
      )}
      {call.output !== undefined && (
        <div className="reasoning__tool-line muted">
          out: <span className="mono">{call.output}</span>
        </div>
      )}
    </li>
  );
}

/**
 * Open while the model is thinking, collapsed once output starts, and always
 * reopenable. Assembled client side from the wire thinking and
 * generation_status events plus the tool arc; the wire has no reasoning
 * delta event, so this shape is what a client can honestly show.
 */
export function ReasoningPanel({
  reasoning,
  toolCalls,
  generationPhase,
}: {
  reasoning: MessageReasoning;
  toolCalls: readonly ToolCall[];
  generationPhase: string | null;
}): ReactElement {
  const thinking = reasoning.status === 'thinking';
  const elapsed =
    reasoning.totalElapsedMs !== undefined
      ? `${(reasoning.totalElapsedMs / 1000).toFixed(1)}s`
      : null;

  return (
    <details className="reasoning" open={thinking || undefined}>
      <summary className="reasoning__summary">
        <span>{thinking ? 'Thinking' : 'Reasoning'}</span>
        {thinking && generationPhase !== null && (
          <span className="mono muted">{generationPhase}</span>
        )}
        {!thinking && elapsed !== null && <span className="mono muted">{elapsed}</span>}
      </summary>
      <div className="reasoning__body">
        {reasoning.thinkingContent !== undefined && (
          <ul className="reasoning__phases">
            {reasoning.thinkingContent.split('\n').map((line: string, index: number) => (
              <li key={`${String(index)}-${line}`} className="mono">
                {line}
              </li>
            ))}
          </ul>
        )}
        {toolCalls.length > 0 && (
          <ul className="reasoning__tools">
            {toolCalls.map((call, index) => (
              <ToolRow key={`${call.tool}-${String(index)}`} call={call} />
            ))}
          </ul>
        )}
        {reasoning.thinkingContent === undefined && toolCalls.length === 0 && (
          <p className="muted reasoning__empty">No visible steps for this response.</p>
        )}
      </div>
    </details>
  );
}
